"use client";

import { Core } from "@faire/design-tokens";
import { getDocumentOrThrow } from "@faire/web/common/globals/getDocument";
import { getWindowOrThrow } from "@faire/web/common/globals/getWindow";
import { Button } from "@faire/web/slate/Button";
import { Opacity } from "@faire/web/slate/Color";
import { IconButton } from "@faire/web/slate/IconButton";
import { StyledIconWrapper } from "@faire/web/slate/IconButton/IconButton";
import { Block } from "@faire/web/slate/icons/foundation/Block";
import { HorizontalEllipsis } from "@faire/web/slate/icons/foundation/HorizontalEllipsis";
import { ListItem } from "@faire/web/slate/ListItem";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import ClickOutside from "@faire/web/ui/ClickOutside";
import { useRefFrom } from "@faire/web/ui/hooks/useRefFrom";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

import { LazyUserFeedbackSubmitModal } from "@faire/retailer/components/UserFeedback/LazyUserFeedbackSubmitModal";

const BUTTON_SIZE = 32;
const DROPDOWN_BUTTON_WIDTH = 150;

export const UserDrivenFeedbackButton: React.FC<{
  brandToken?: string;
}> = ({ brandToken }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showFeedbackReasonModal, setShowFeedbackReasonModal] = useState(false);
  const { containerRef, position, portalContainer } = usePortal();

  return (
    <StyledContainer ref={containerRef} onClick={(e) => e.preventDefault()}>
      <StyledIconButton
        size="S"
        variant="tertiary"
        aria-label="more"
        shape="circle"
        data-test-id={`user-driven-feedback-button-${brandToken}`}
        onClick={() => setShowDropdown((prev) => !prev)}
      >
        <HorizontalEllipsis fontSize="16px" />
      </StyledIconButton>
      {position
        ? createPortal(
            <ClickOutside
              onClickOutside={() => setShowDropdown(false)}
              style={{
                position: "absolute",
                top: position.top + BUTTON_SIZE + "px",
                left:
                  position.left - DROPDOWN_BUTTON_WIDTH + BUTTON_SIZE + "px",
                width: position.right - position.left + "px",
                zIndex: 100,
              }}
            >
              {showDropdown ? (
                <ProductTileUserFeedbackDropdown
                  onHideBrandClick={() => setShowFeedbackReasonModal(true)}
                />
              ) : null}
            </ClickOutside>,
            portalContainer
          )
        : null}
      {showFeedbackReasonModal && brandToken ? (
        <LazyUserFeedbackSubmitModal
          brandToken={brandToken}
          onClose={() => setShowFeedbackReasonModal(false)}
        />
      ) : null}
    </StyledContainer>
  );
};

const usePortal = () => {
  const [position, setPosition] = useState<
    { top: number; left: number; right: number } | undefined
  >();
  const containerRef = useRef<HTMLDivElement>(null);
  const portalContainer = useRefFrom(() =>
    getDocumentOrThrow().createElement("div")
  ).current;
  useEffect(() => {
    getDocumentOrThrow().body.appendChild(portalContainer);
    return () => {
      getDocumentOrThrow().body.removeChild(portalContainer);
    };
  }, [portalContainer]);

  useEffect(() => {
    const offset = containerRef.current?.getBoundingClientRect();
    const scrollY = getWindowOrThrow().scrollY;
    if (offset) {
      setPosition({
        top: offset.top + scrollY,
        left: offset.left,
        right: offset.right,
      });
    }
  }, []);
  return {
    containerRef,
    position,
    portalContainer,
  };
};

const ProductTileUserFeedbackDropdown: React.FC<{
  onHideBrandClick: () => void;
}> = ({ onHideBrandClick }) => {
  const getMiddle = () => {
    return (
      <StyledTextButton onClick={onHideBrandClick}>
        <Typography>
          <StrictLocalMsg
            defaultMessage="Hide brand"
            description={{
              text: "A button text for hiding a brand on a grid after user click it.",
              img: "https://cdn.faire.com/fastly/bb98770d56b9ad059c8bea16ec933bc238bdb6ddf37857253a36b7b058c28fcd.png",
            }}
          />
        </Typography>
      </StyledTextButton>
    );
  };

  const getStart = () => <Block />;
  return (
    <StyledListItem
      start={getStart()}
      middle={getMiddle()}
      end={null}
      size="small"
    />
  );
};

// The ${StyledIconWrapper} part is a small hack to make sure the button shows correct background shadow when user hover it.
const StyledIconButton = styled(IconButton)`
  width: ${BUTTON_SIZE}px;
  height: ${BUTTON_SIZE}px;
  ${StyledIconWrapper} {
    height: ${BUTTON_SIZE}px;
  }
`;

const StyledListItem = styled(ListItem)`
  width: ${DROPDOWN_BUTTON_WIDTH}px;
  height: ${getSpacing("7x")};
  border-bottom: 0px;
  box-shadow: 0 4px 8px ${Core.surface.default}${Opacity.O_15};
`;

const StyledContainer = styled.div`
  height: ${BUTTON_SIZE}px;
  width: ${BUTTON_SIZE}px;
  margin-top: -8px;
`;

const StyledTextButton = styled(Button).attrs({
  variant: "tertiary",
})`
  width: ${DROPDOWN_BUTTON_WIDTH}px;
  &&& {
    justify-content: flex-start;
    width: 100%;
    text-decoration: none;

    ${responsiveMediaQueries.mobile} {
      justify-content: space-between;
    }

    &:hover {
      text-decoration: none;
    }
  }
`;
